import Styles from './NodeAPI.module.scss';
import { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// module imports
import _ from 'lodash';
import axios from 'axios';
import { parse, print } from 'graphql';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Avatar, Typography } from '@mui/material';
import { pink } from '@mui/material/colors';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import StraightIcon from '@mui/icons-material/Straight';

// component imports
import { useAPI, AuthContext, Feedback, ViewContext } from 'components/lib';
import InfoDrawer from 'components/global/NodeLayout/InfoDrawer';
import NodeProgress from 'components/global/NodeLayout/NodeProgress/NodeProgress';
import { NodeTopostman, LightTooltip } from 'components/global/NodeLayout/NodeTopostman';
import { NodeTimeline } from 'components/global/NodeLayout/NodeTimeline';
import { ConfirmationCard } from 'components/global/ConfirmationCard/ConfirmationCard';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { Joyride } from 'components/walkthrough/walkthrough';
import { HelperChat } from 'components/global/HelperChat/HelperChat';
import { BackConfirmationCard } from 'components/global/BackConfirmationCard/BackConfirmationCard';

// utils
import { decoder } from 'utils/helpers/decoder';
import Soundcheck from 'assets/sounds/try-again.mp3';
import RevealAnswerKey from 'components/RevealAnswerKey/RevealAnswerKey';
import { InstructionPane } from 'components/global/InstructionPane/InstructionPane';
import { copyEnabledChecker } from 'utils/helpers/utility';
const finalAudio = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}/media/git/voice/finish_node.mp3`;

export function NodeAPI(props) {

    const { node_id } = useParams();
    const { lab_id } = useParams();
    const { resource } = useParams();
    const context = useContext(AuthContext);

    const { data, loading } = useAPI(`/api/labs/${lab_id}/nodes/${node_id}/${resource}`, 'GET');
    const [currentStep, setCurrentStep] = useState(0);
    const [nodeFinished, setNodeFinished] = useState(false);
    const [steps, setSteps] = useState();
    const [nodeInfo, setNodeInfo] = useState();
    const [isMuted, setIsMuted] = useState(true);
    const [displayData, setDisplayData] = useState();
    const [isGraphQL, setIsGraphQL] = useState(false);
    const [showFinishConfirm, setShowFinishConfirm] = useState(false);
    const roundedPercentage = nodeFinished ? 100 : Math.ceil(currentStep / nodeInfo?.numOfSteps * 100);
    const isCopyEnabled = copyEnabledChecker(context?.user)
    const [copied, setCopied] = useState(false);

    const [apiState, setAPIState] = useState({
        method: '',
        endpoint: '',
        headers: {},
        environment: {},
        body: '',
        response: '',
        generatedToken: '',
        query: '',
        variable: {},
        allCheck: ''
    });

    const [buttonState, setButtonState] = useState({
        sendDisabled: false,
        validateDisabled: false
    });

    const [startLab, setStartLab] = useState(true);
    const navigate = useNavigate();

    const currentRef = useRef(null);

    const [drawerOpen, setDrawerOpen] = useState(false);

    const [showReveal, setShowReveal] = useState(false);
    const [maxUnlocked, setMaxUnlocked] = useState(0);
    const audioRef = useRef(null);
    const finalAudioRef = useRef(null);
    const viewContext = useContext(ViewContext)


    const handleDrawer = () => {
        setDrawerOpen(!drawerOpen);
    };

    function moveToNext(validateGoal, allValid = false) {
        const command = steps[currentStep].command.toLowerCase();

        if (validateGoal === "inputs") {
            if (!allValid && nodeInfo.is_assessment !== 1) {
                return;
            }

        } else {
            if (!command.startsWith(validateGoal) && nodeInfo.is_assessment !== 1) {
                return;
            }
        }

        setAPIState(prev => ({ ...prev, response: "" }));

        if (currentStep !== nodeInfo.numOfSteps - 1) {
            setCurrentStep(currentStep + 1);

            const myElement = document.getElementById("currenttimelineHolder");
            if (myElement) {
                const topPos = myElement.offsetTop;
                document.getElementById("timelinescrolldiv").scrollTop = topPos;
            }
        } else {
            setNodeFinished(true);
        }
        setButtonState({
            sendDisabled: false,
            validateDisabled: false
        });
    }

    function moveToErrorSimple(item) {
        const wastedAttemptAudio = new Audio(Soundcheck);
        wastedAttemptAudio.play();
        toast.dark(props.t('Labs.Nodes.NodeAPI.toast.error.simple', { item: item }), {
            type: 'error',
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    }

    const compareQuery = () => {
        if (steps[currentStep].exp_input['query'].length === 0) {
            return true;
        }
        try {
            const apiStateQuery = apiState.query.trim();
            const expQuery = steps[currentStep].exp_input['query'].trim();

            if (nodeInfo.name === 'GraphQL Basics') {
                try {
                    const expQueryArray = expQuery.split('{')
                    const apiStateArray = apiStateQuery.split('{')

                    const queryMatch1 = _.isEqual(expQueryArray[0].trim(), apiStateArray[0].trim())

                    const apiQueryContent = apiStateArray[1].replace(/\s/g, '').split("")
                    const expQueryContent = expQueryArray[1].replace(/\s/g, '').split("")

                    const queryMatch2 = _.isEqual(expQueryContent, apiQueryContent)

                    const isQueryMatch = queryMatch1 && queryMatch2

                    if (!isQueryMatch) {
                        return false;
                    }
                    return true;
                } catch {
                    return false;
                }

            } else {
                try {
                    const apiQueryFormatted = print(parse(apiStateQuery));
                    const expQueryFormatted = print(parse(expQuery));

                    const expArr = expQueryFormatted.split(' ');
                    const apiArr = apiQueryFormatted.split(' ');

                    const isQueryMatch = expArr.every((element) => apiArr.indexOf(element) !== -1);

                    if (!isQueryMatch) {
                        if (nodeInfo.is_assessment === 1) {
                            return false;
                        } else {
                            return false;
                        }
                    }
                    return isQueryMatch;
                } catch {
                    return false;
                }
            }
        } catch {
            return false;
        }
    };

    const compareVariables = () => {
        const apiStateVariable = apiState.variable;
        if (_.isEqual(steps[currentStep].exp_input['variables'], {})) {
            return true;
        }
        try {
            const isVariableMatch = _.isEqual(apiStateVariable, steps[currentStep].exp_input['variables']);
            if (!isVariableMatch) {
                return false;
            }
            return isVariableMatch;
        } catch {
            return false;
        }
    };

    const compareMethod = () => {
        const expected = steps[currentStep].exp_input['method'];
        const actual = apiState.method || "";

        return expected === actual;
    };

    const compareEndpoint = () => {
        const expectedRaw = steps[currentStep].exp_input['endpoint'];
        let actual = apiState.endpoint ? apiState.endpoint.trim() : '';
        const environment = apiState.environment || {};
        if (!expectedRaw) return actual === '';
        const expected = expectedRaw.replace(/{{(.*?)}}/g, (_, key) => environment[key.trim()] || '');
        actual = actual.replace(/{{(.*?)}}/g, (_, key) => environment[key.trim()] || '');
        if (!expected) return actual === '';
        try {
            const regex = new RegExp(expected);
            return regex.test(actual);
        } catch (e) {
            return false;
        }
    };

    const compareBody = () => {
        let expected = steps[currentStep].exp_input['body'] || {};
        let actual = apiState.body;

        try {
            if (typeof actual === "string") {
                actual = actual.trim() === "" ? {} : JSON.parse(actual);
            } else if (typeof actual !== "object" || actual === null) {
                actual = {};
            }
        } catch (err) {
            return false;
        }

        if (_.isNil(actual)) actual = {};
        if (_.isNil(expected)) expected = {};

        return _.isEqual(expected, actual);
    };

    const compareHeaders = () => {
        let expected = steps[currentStep].exp_input['headers'];
        const actual = apiState.headers || {};

        if (Array.isArray(expected)) {
            expected = expected.reduce((acc, header) => {
                if (header.key && header.value) {
                    acc[header.key] = header.value;
                } else if (header.headerKey && header.headerValue) {
                    acc[header.headerKey] = header.headerValue;
                }
                return acc;
            }, {});
        } else if (!expected) {
            expected = {};
        }

        if (Object.keys(expected).length === 0) {
            return Object.keys(actual).length === 0;
        }

        for (const [key, expectedVal] of Object.entries(expected)) {
            const actualVal = actual[key];
            if (!actualVal) return false;

            const regex = new RegExp(expectedVal);
            if (!regex.test(actualVal)) {
                return false;
            }
        }

        return true;
    };

    const compareEnvironment = () => {
        const expectedEnv = steps[currentStep].exp_input['environment'] || {};
        const actualEnv = apiState.environment || {};

        if (Object.keys(expectedEnv).length === 0) {
            return Object.keys(actualEnv).length === 0;
        }

        for (const [key, expectedVal] of Object.entries(expectedEnv)) {
            const actualVal = actualEnv[key];
            if (!actualVal) return false;

            const regex = new RegExp(expectedVal);
            if (!regex.test(actualVal)) {
                return false;
            }
        }

        return true;
    };

    const compareResponse = () => {
        const expected = steps[currentStep].exp_input['response'];
        const actual = apiState.response;

        return expected === actual;
    };

    const compareAll = () => {
        const methodOk = compareMethod();
        const endpointOk = compareEndpoint();
        const headersOk = compareHeaders();
        const bodyOk = compareBody();
        const envOk = compareEnvironment();

        let isCorrect = methodOk && endpointOk && headersOk && bodyOk && envOk;

        if (isGraphQL) {
            const queryOk = compareQuery();
            const varsOk = compareVariables();
            isCorrect = isCorrect && queryOk && varsOk;
        }

        return isCorrect;
    };

    const [validationState, setValidationState] = useState({
        isValidated: false,
        validationResults: {
            method: false,
            endpoint: false,
            headers: false,
            body: false,
            environment: false,
            query: false,
            variables: false
        }
    });

    const validateAllInputs = () => {
        const results = {
            method: compareMethod(true),
            endpoint: compareEndpoint(true),
            headers: compareHeaders(true),
            body: compareBody(true),
            environment: compareEnvironment(true),
            response: compareResponse(),
        };

        if (isGraphQL) {
            results.query = compareQuery(true);
            results.variables = compareVariables(true);
        }

        const allValid = Object.values(results).every(result => result === true);

        setValidationState({
            isValidated: true,
            validationResults: results
        });

        if (!allValid) {
            const invalidFields = Object.entries(results)
                .filter(([_, isValid]) => !isValid)
                .map(([field]) => field);

            const wastedAttemptAudio = new Audio(Soundcheck);
            wastedAttemptAudio.play();

            toast.dark(
                `${props.t('Labs.Nodes.NodeAPI.errorMsg')}: ${invalidFields.join(", ")}`,
                {
                    type: 'error',
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                }
            );

            setButtonState({
                sendDisabled: false,
                validateDisabled: true
            });
        } else {
            if (nodeInfo.is_assessment !== 1) {
                moveToNext("inputs", allValid);
            } else {
                toast.dark(props.t('Labs.Nodes.NodeAPI.toast.success'), {
                    type: 'success',
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
                moveToNext("inputs", allValid);
            }
        }

        return allValid;
    };

    const handleSendClick = () => {
        setButtonState({
            sendDisabled: true,
            validateDisabled: false
        });
    };

    useEffect(() => {
        setValidationState({
            isValidated: false,
            validationResults: {
                method: false,
                endpoint: false,
                headers: false,
                body: false,
                environment: false,
                query: false,
                variables: false
            }
        });

        setButtonState({
            sendDisabled: false,
            validateDisabled: false
        });
    }, [currentStep]);

    useEffect(() => {
        if (!startLab) {
            if (apiState.response !== -1) {
                if (
                    apiState?.allCheck &&
                    steps[currentStep].exp_input['response'] === apiState.response
                ) {
                }
            }
        }
    }, [apiState.response]);

    useEffect(() => {
        if (data) {
            const jsonData = decoder(data);
            setSteps(jsonData.steps.steps);
            setNodeInfo(jsonData);
            jsonData.numOfSteps = jsonData.steps.steps.length;
            setDisplayData(jsonData.content.reference_data);
            setIsGraphQL(jsonData.resource.resource_name === 'graphql')
            document.title = props.t('Labs.Nodes.NodeAPI.title', { nodeName: jsonData.name })
        }
    }, [data]);

    useEffect(() => {
        if (!steps) return;

        if (audioRef.current) {
            audioRef.current.pause();
        }

        const audioLink = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}${steps[currentStep]?.voice}`;
        if (audioLink && !isMuted) {
            const newAudio = new Audio(audioLink);
            audioRef.current = newAudio;
            newAudio.play();
        }

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, isMuted]);

    useEffect(() => {
        if (!nodeFinished) return;

        if (audioRef.current) {
            audioRef.current.pause();
        }

        const audio = new Audio(finalAudio);
        finalAudioRef.current = audio;
        audio.play();

        viewContext.notification.show(
            props.t('Labs.Nodes.NodeAPI.end'),
            'info',
            false
        );

        return () => {
            if (finalAudioRef.current) {
                finalAudioRef.current.pause();
            }
        };

    }, [nodeFinished])

    const handleVoiceClick = () => {
        setIsMuted(!isMuted);
    };

    const navigateBack = () => {
        navigate(-1);
    };

    const startLabClick = async () => {
        const options = {
            method: 'POST',
            url: `/api/user/labs/${lab_id}/nodes/${node_id}/start`,
            headers: {
                accept: 'application/json',
            },
        };
        await axios.request(options)
            .then((response) => {
                new Promise((resolve) => setTimeout(resolve, 100));
                setStartLab(false);
                if (context?.user?.stop_walkthrough?.includes(nodeInfo?.resource?.lab_resource_id) || nodeInfo.is_assessment === 1) {
                    setIsMuted(!isMuted)
                }
            }).catch((err) => {
                if (err.response.status === 403) {
                    navigate('/nopermission');
                } else {
                    navigate('/oops');
                }
            });
    };

    const handleNext = () => {
        const options = {
            method: 'POST',
            url: `/api/user/labs/${lab_id}/nodes/${node_id}/end`,
            headers: {
                accept: 'application/json',
            },
            ...(nodeInfo.is_assessment === 1 && { data: { completionPercent: roundedPercentage } }),
        };
        axios
            .request(options)
            .then(async function (response) {
                await new Promise((resolve) => setTimeout(resolve, 50));
                if (nodeInfo.is_assessment === 1) {
                    navigate(`/labs/${lab_id}/nodes`);
                } else {
                    navigate(`/labs/${lab_id}/nodes/${node_id}/quiz/attempt`);
                }
            })
            .catch((err) => {
                let status = err.response.status;
                console.log('error', status);
                navigate('/oops');
            });
    };
    const resetWalkthrough = (data) => {
        if (data.status === 'finished' | data.action === 'skip' | data.action === 'tour:end') {
            setIsMuted(!isMuted)
        }
    }
    useEffect(() => {
        const resourceIncluded = context?.user?.stop_walkthrough?.includes(nodeInfo?.resource?.lab_resource_id);
        if (resourceIncluded && !startLab) {
            setIsMuted(!isMuted)
        }
    }, [context?.user?.stop_walkthrough])

    const showConfirm = () => {
        setShowFinishConfirm(true);
    }

    const handleConfirmClick = () => {
        handleNext();
    }

    useEffect(() => {
        setMaxUnlocked(prev => Math.max(prev, currentStep));
    }, [currentStep]);

    const handleCopy = async (e) => {
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
        }, 2000);
        toast.dark(props.t('Labs.Nodes.NodeAutomation.toast.warn'), {
            type: 'warning',
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    };

    const handleCopyDisabled = (e) => {
        e.preventDefault();
    }

    return (
        <>
            {loading ? (
                <div>
                    <LoadingBackdrop isOpen={true} />
                </div>
            ) : steps ? (
                <>
                    <ToastContainer
                        id="warning-toast"
                        position="top-right"
                        autoClose={10000}
                        hideProgressBar={false}
                        newestOnTop
                        closeOnClick
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                        limit={4}
                    />
                    <BackConfirmationCard
                        isSaveAvailable={true}
                    />
                    <div id="ancestorContainer" className={Styles.ancestorContainer}>
                        <ConfirmationCard
                            title={nodeInfo.is_assessment ?
                                props.t('Labs.Nodes.NodeAPI.confirm.start.title.assessment')
                                : props.t('Labs.Nodes.NodeAPI.confirm.start.title.default')
                            }
                            body={props.t('Labs.Nodes.NodeAPI.confirm.start.body')}
                            showCard={startLab}
                            setShowCard={setStartLab}
                            handleCancel={navigateBack}
                            handleConfirm={startLabClick}
                        />
                        {!startLab && nodeInfo.is_assessment !== 1 ?
                            <Joyride
                                open={nodeInfo?.resource?.walkthrough?.open}
                                run={nodeInfo?.resource?.walkthrough?.run}
                                steps={nodeInfo?.resource?.walkthrough?.steps || []}
                                resourceId={nodeInfo?.resource?.lab_resource_id}
                                disableOverlay={true}
                                callback={resetWalkthrough}
                            />
                            : null}
                        <div id="titleContainer" className={Styles.titleContainer}>
                            <div style={{ display: 'flex' }}>
                                <Avatar
                                    sx={{
                                        bgcolor: pink[500],
                                        alignSelf: 'center',
                                        mr: 1,
                                        width: 55,
                                        height: 55,
                                    }}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                        <StraightIcon sx={{ width: 42, height: 37 }} />
                                        <StraightIcon sx={{ width: 42, height: 37, ml: '-24px', transform: 'rotate(180deg)' }} />
                                    </span>
                                </Avatar>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        textTransform: 'uppercase',
                                        color: '#FFFFFF',
                                    }}
                                >
                                    {nodeInfo.name}
                                </Typography>

                            </div>
                            <span style={{ display: 'flex', flexDirection: 'row' }}>
                                <LightTooltip
                                    title={nodeInfo.is_assessment
                                        ? ((roundedPercentage > 70) || nodeFinished ?
                                            props.t('Labs.Nodes.NodeAPI.tooltip.finish.done')
                                            : props.t('Labs.Nodes.NodeAPI.tooltip.finish.assessment')
                                        )
                                        : (nodeFinished ?
                                            props.t('Labs.Nodes.NodeAPI.tooltip.finish.done')
                                            : props.t('Labs.Nodes.NodeAPI.tooltip.finish.info')
                                        )
                                    }
                                    arrow
                                    placement="left"
                                    classes={{
                                        tooltip: {
                                            backgroundColor: '#fff',
                                            color: 'rgba(0, 0, 0, 0.87)',
                                            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
                                            fontSize: 11,
                                        },
                                    }}
                                >
                                    <span style={{ display: "flex", alignItems: "center" }}>
                                        <button
                                            variant="extended"
                                            className={Styles.finishButton}
                                            onClick={() => {
                                                nodeInfo.is_assessment ? showConfirm() : handleNext()
                                            }}
                                            disabled={
                                                !nodeInfo.is_assessment
                                                    ? !nodeFinished
                                                    : roundedPercentage < 70
                                            }
                                        >
                                            <KeyboardDoubleArrowRightIcon />
                                            {props.t('Labs.Nodes.NodeAPI.button')}
                                        </button>
                                    </span>
                                </LightTooltip>
                                <ConfirmationCard
                                    title={(roundedPercentage > 70 && roundedPercentage < 100) ?
                                        props.t('Labs.Nodes.NodeAPI.confirm.completed.title.warn')
                                        : props.t('Labs.Nodes.NodeAPI.confirm.completed.title.default')
                                    }
                                    body={(roundedPercentage > 70 && roundedPercentage < 100) ?
                                        props.t('Labs.Nodes.NodeAPI.confirm.completed.body.warn')
                                        : props.t('Labs.Nodes.NodeAPI.confirm.completed.body.default')
                                    }
                                    showCard={showFinishConfirm}
                                    setShowCard={setShowFinishConfirm}
                                    handleConfirm={handleConfirmClick}
                                />

                                <NodeProgress
                                    lab_id={lab_id}
                                    node_id={node_id}
                                    currentStep={currentStep}
                                    setDrawerOpen={setDrawerOpen}
                                    drawerOpen={drawerOpen}
                                    setCurrentStep={setCurrentStep}
                                    initialUserProgress={nodeInfo.userProgress}
                                    steps={steps}
                                    data={data}
                                    resource={nodeInfo?.resource}
                                    onReveal={() => setShowReveal(true)}
                                    nodeInfo={nodeInfo}
                                    maxUnlocked={maxUnlocked}
                                    setMaxUnlocked={setMaxUnlocked}
                                />
                            </span>
                        </div>
                        <div id="midContainer" className={Styles.midContainer}>
                            <div id="topostContainer" className={Styles.topostContainer}>
                                <NodeTopostman
                                    setAPIState={setAPIState}
                                    apiState={apiState}
                                    steps={steps}
                                    current={currentStep}
                                    isGraphQL={isGraphQL}
                                    is_assessment={nodeInfo.is_assessment}
                                    onCompare={compareAll}
                                    onValidate={validateAllInputs}
                                    validationState={validationState}
                                    buttonState={buttonState}
                                    onSendClick={handleSendClick}
                                />
                            </div>
                            <div data-testid="nodeApi-midContainer-instructionsContainer" id="instructionsContainer" className={Styles.instructionsContainer}>
                                <InstructionPane
                                    props={props}
                                    steps={steps}
                                    currentStep={currentStep}
                                    isMuted={isMuted}
                                    setIsMuted={setIsMuted}
                                    onHandleVoiceClick={handleVoiceClick}
                                    onHandleCopy={handleCopy}
                                    onHandleCopyDisabled={handleCopyDisabled}
                                    copied={copied}
                                    showLineNumbers={false}
                                    nodeInfo={nodeInfo}
                                    nodeAPI={true}
                                    theme='light'
                                    isCopyEnabled={isCopyEnabled}
                                    displayCopyButton={false}
                                />
                            </div>
                            <span className={Styles.Timeline}>
                                <NodeTimeline
                                    nodeFinished={nodeFinished}
                                    currentStep={currentStep}
                                    steps={steps}
                                    currentRef={currentRef}
                                    hideToolTip={false}
                                    collapsable={true}
                                    setCurrentStep={setCurrentStep}
                                    maxUnlocked={maxUnlocked}
                                />
                            </span>
                        </div>
                    </div>
                    {(!startLab && !nodeFinished && nodeInfo.is_assessment !== 1 && nodeInfo?.resource?.config?.chatbotTopic) && (
                        <HelperChat
                            active={props.aiChat}
                            topic={nodeInfo?.resource?.config?.chatbotTopic}
                        />
                    )}
                    <InfoDrawer
                        handleDrawer={handleDrawer}
                        drawerOpen={drawerOpen}
                        titleText={props.t('Labs.Nodes.NodeAPI.reference', { LabName: nodeInfo.labName })}
                        displayData={displayData}
                    />
                    {nodeFinished && <Feedback isInNode={true} nodeName={nodeInfo.name} labName={nodeInfo.labName} />}

                    <RevealAnswerKey
                        showAnswerKeyDialog={showReveal}
                        setShowAnswerKeyDialog={setShowReveal}
                        steps={steps}
                        currentStep={currentStep}
                        language={"JSON"}
                        nodeId={node_id}
                        nodeName={nodeInfo?.name}
                        labTitle={nodeInfo?.labName}
                    />
                </>
            ) : (
                <div>
                    <LoadingBackdrop isOpen={true} />
                </div>
            )
            }
        </>
    );
}
